import React, { FormEvent, useState } from 'react'
import clsx from 'clsx'

import { createStyles, makeStyles } from '@material-ui/core/styles'
import { Box, Dialog, DialogContent, FormHelperText, ListItem, ListItemText } from '@material-ui/core'

import { ButtonLoader } from './ButtonLoader'
import { DropdownItemProps } from './DropdownButton'
import { MatchStoreProductPropSelector } from './MatchStoreProductPropSelector'
import { MatchMsProductPropSelector, msProductMatchingProps } from './MatchMsProductPropSelector'

import { useBoolState } from '../hooks/commonHooks'
import { useMessages } from '../hooks/snackbarHooks'
import { useDefaultMenuItemStyles } from '../hooks/defaultSelectStylesHook'

import { EnumConstraint } from '../common/tsUtils'
import { pluralize } from '../common/stringUtility'

import { StoreIntegrationType } from '../types/integrationTypeUtils'
import { MsProductProp } from '../server/mpsklad_core/Models/MsProductProp'

export type MatchProductsButtonBaseProps = {
  accountIdFrom: number

  accountIdFromTitle: string
}

export type MatchProductsButtonProps<TFromProp extends number & EnumConstraint<TFromProp>> =
  DropdownItemProps
  & MatchProductsButtonBaseProps
  & {
  fromIntegrationType: StoreIntegrationType

  fromProps: ReadonlyArray<readonly [TFromProp, string]>

  submit: (fromProp: TFromProp, toProp: MsProductProp, accountIdFrom: number) => Promise<number>
}

export const MatchProductsButton =
  <TFromProp extends number & EnumConstraint<TFromProp>>(
    {
      fromIntegrationType, fromProps,
      submit, setClosed,
      accountIdFrom, accountIdFromTitle
    }: MatchProductsButtonProps<TFromProp>) => {
    const classes = useMatchProductsButtonStyles()
    const menuItemClasses = useDefaultMenuItemStyles()

    const {showSuccess, showWarning} = useMessages()

    const [isOpen, openModal, closeModal] = useBoolState()

    const [isLoading, setLoading, setDone] = useBoolState()

    const [fromProp, setFromProp] = useState(fromProps[0][0])

    const [toProp, setToProp] = useState<MsProductProp>(msProductMatchingProps[0][0])

    const onClose = () => {
      closeModal()
      setClosed()
    }

    const onSubmit =
      async (e: FormEvent) => {
        e.preventDefault()
        setLoading()

        const productCount = await submit(fromProp, toProp, accountIdFrom)

        if (productCount > 0) {
          showSuccess(pluralize(productCount, '$ товар сопоставлен', '$ товара сопоставлено', '$ товаров сопоставлено'))
        } else {
          showWarning('Товары с совпадающими полями не найдены')
        }

        setDone()
        onClose()
      }

    return <>
      <ListItem className={menuItemClasses.menuItem} button onClick={openModal}>
        <ListItemText primary={`С ${accountIdFromTitle}`}/>
      </ListItem>

      <Dialog open={isOpen} onClose={closeModal} classes={{paper: classes.dialogPaper}}>
        <Box className={classes.title}>
          {`Сопоставление товаров ${accountIdFromTitle} и МоегоСклада`}
        </Box>

        <DialogContent>
          <Box paddingY={1}>
            <form onSubmit={onSubmit}>
              <Box>
                <MatchStoreProductPropSelector
                  integrationType={fromIntegrationType}
                  storeProp={fromProp}
                  storeProps={fromProps}
                  onChange={setFromProp}
                />
              </Box>

              <Box marginTop={3}>
                <MatchMsProductPropSelector msProp={toProp} onChange={setToProp}/>
              </Box>

              <Box marginTop={3}>
                <FormHelperText className={classes.helperText}>
                  Сопоставление может занять пару минут для большого количества товаров.
                </FormHelperText>
              </Box>

              <Box className={classes.actions}>
                <button
                  type="button"
                  className={clsx(classes.cancelButton, 'default-button')}
                  onClick={onClose}
                  disabled={isLoading}
                >
                  <p>Отменить</p>
                </button>

                <button
                  type="submit"
                  className={clsx(classes.saveButton, 'default-button')}
                  disabled={isLoading}
                >
                  <p>
                    {
                      isLoading
                      ? <ButtonLoader text="Сопоставляем..."/>
                      : 'Сопоставить'
                    }
                  </p>
                </button>
              </Box>
            </form>
          </Box>
        </DialogContent>
      </Dialog>
    </>
  }

export const useMatchProductsButtonStyles =
  makeStyles((/*theme: Theme*/) =>
    createStyles({
      dialogPaper: {
        // At the top
        alignSelf: 'flex-start'
      },
      dash: {
        color: '#3987CF',
        marginRight: 3
      },
      title: {
        padding: 20,
        fontSize: '20px',
        fontWeight: 'bold',
        color: '#1F364D'
      },
      saveButton: {
        width: 160,
        height: 40,
        color: '#FFFFFF',
        alignItems: 'center',
        marginLeft: 10
      },
      cancelButton: {
        width: 160,
        height: 40,
        color: '#FFFFFF',
        backgroundColor: '#D2D2D2',
        alignItems: 'center',
        '&:hover': {
          backgroundColor: '#BCBCBC'
        }
      },
      actions: {
        display: 'flex',
        justifyContent: 'center !important'
      },
      helperText: {
        fontSize: '11px',
        fontWeight: 400,
        lineHeight: '12px',
        textAlign: 'left',
        marginTop: 5
      },
      settingsTopic: {
        borderRadius: 12,
        width: '100%'
      },
      label: {
        '&, &.MuiFormLabel-root': {
          fontSize: '12px',
          fontWeight: 600,
          lineHeight: '14.4px',
          textAlign: 'left',
          color: '#1F364D'
        }
      },
      autoMatchButtonEnabled: {
        width: 121
      }
    }))